import React, { useState } from "react";
import { Grid, IconButton, Tooltip, CircularProgress } from "@mui/material";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { PictureAsPdf, InsertDriveFile, TableChart } from "@mui/icons-material";
import { getPayInData } from "./api";

const PayInDownloadButtons = ({ fromDate, toDate, searchQuery }) => {
  const [loading, setLoading] = useState(false);

  const fetchAllData = async () => {
    let allData = [];
    let currentPage = 0;
    let hasMoreData = true;
    const downloadPageSize = 1000; // Fetch more data per request for download

    while (hasMoreData) {
      const response = await getPayInData({
        page: currentPage,
        size: downloadPageSize,
        fromDate,
        toDate,
        searchQuery,
      });

      const pageData = response?.data?.body?.data;
      if (!pageData || pageData.length === 0) {
        hasMoreData = false;
      } else {
        allData = [...allData, ...pageData];
        currentPage++;
      }

      // If received less than page size, we've reached the end
      if (pageData && pageData.length < downloadPageSize) {
        hasMoreData = false;
      }
    }

    return allData;
  };

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);
      const allData = await fetchAllData();

      if (allData.length === 0) {
        console.log("No data available to download");
        return;
      }

      const doc = new jsPDF('landscape');
      doc.text("Pay In Report", 14, 15);

      const columns = [
        "Sr. No.",
        "Txn ID",
        "UTR No",
        "Date",
        "PayIn Amount",
        "Amount",
        "Status",
        "Remark"
      ];

      const rows = allData.map((row, index) => [
        index + 1,
        row.txnid || "N/A",
        row.easepayid || "N/A",
        row.addedon || "N/A",
        row.netAmountDebit || "N/A",
        row.amount || "N/A",
        row.status === "success" ? "Successful" : "Failed",
        row.remark || "N/A"
      ]);

      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 25,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [2, 123, 254],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
        },
      });

      const currentDateTime = new Date().toISOString().split('T')[0];
      doc.save(`pay_in_report_${currentDateTime}.pdf`);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const allData = await fetchAllData();

      if (allData.length === 0) {
        console.log("No data available to download");
        return;
      }

      const excelData = allData.map((row, index) => ({
        "Sr. No.": index + 1,
        "Txn ID": row.txnid || "N/A",
        "UTR No": row.easepayid || "N/A",
        "Date": row.addedon || "N/A",
        "PayIn Amount": row.netAmountDebit || "N/A",
        "Amount": row.amount || "N/A",
        "Status": row.status === "success" ? "Successful" : "Failed",
        "Remark": row.remark || "N/A"
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "PayIn Report");

      // Set column widths
      const columnWidths = [
        { wch: 8 },  // Sr. No.
        { wch: 15 }, // Txn ID
        { wch: 15 }, // UTR No
        { wch: 20 }, // Date
        { wch: 15 }, // PayIn Amount
        { wch: 12 }, // Amount
        { wch: 12 }, // Status
        { wch: 20 }, // Remark
      ];
      worksheet['!cols'] = columnWidths;

      const currentDateTime = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `pay_in_report_${currentDateTime}.xlsx`);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setLoading(true);
      const allData = await fetchAllData();

      if (allData.length === 0) {
        console.log("No data available to download");
        return;
      }

      const csvData = allData.map((row, index) => ({
        "Sr. No.": index + 1,
        "Txn ID": row.txnid || "N/A",
        "UTR No": row.easepayid || "N/A",
        "Date": row.addedon || "N/A",
        "PayIn Amount": row.netAmountDebit || "N/A",
        "Amount": row.amount || "N/A",
        "Status": row.status === "success" ? "Successful" : "Failed",
        "Remark": row.remark || "N/A"
      }));

      // Convert to CSV string
      const headers = Object.keys(csvData[0]);
      const csvString = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => row[header]).join(','))
      ].join('\n');

      // Create and download CSV file
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const currentDateTime = new Date().toISOString().split('T')[0];
      saveAs(blob, `pay_in_report_${currentDateTime}.csv`);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      item
      xs={12}
      md={6}
      sx={{
        display: "flex",
        justifyContent: { xs: "center", md: "flex-end" },
        alignItems: "center",
        gap: 2,
      }}
    >
      <Tooltip title="Download as PDF" arrow>
        <IconButton
          onClick={handleDownloadPDF}
          disabled={loading}
          sx={{
            color: "red",
            backgroundColor: "rgba(255, 0, 0, 0.1)",
            border: "2px solid rgba(255, 0, 0, 0.3)",
            borderRadius: "15px",
            transition: "0.3s",
            backdropFilter: "blur(5px)",
            "&:hover": {
              backgroundColor: "red",
              color: "white",
              boxShadow: "0px 4px 15px rgba(255, 0, 0, 0.7)",
              transform: "scale(1.1)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : <PictureAsPdf fontSize="medium" />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Download as CSV" arrow>
        <IconButton
          onClick={handleDownloadCSV}
          disabled={loading}
          sx={{
            color: "blue",
            backgroundColor: "rgba(0, 0, 255, 0.1)",
            border: "2px solid rgba(0, 0, 255, 0.3)",
            borderRadius: "15px",
            transition: "0.3s",
            backdropFilter: "blur(5px)",
            "&:hover": {
              backgroundColor: "blue",
              color: "white",
              boxShadow: "0px 4px 15px rgba(0, 0, 255, 0.7)",
              transform: "scale(1.1)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : <InsertDriveFile fontSize="medium" />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Download as Excel" arrow>
        <IconButton
          onClick={handleDownloadExcel}
          disabled={loading}
          sx={{
            color: "green",
            backgroundColor: "rgba(0, 128, 0, 0.1)",
            border: "2px solid rgba(0, 128, 0, 0.3)",
            borderRadius: "15px",
            transition: "0.3s",
            backdropFilter: "blur(5px)",
            "&:hover": {
              backgroundColor: "green",
              color: "white",
              boxShadow: "0px 4px 15px rgba(0, 128, 0, 0.7)",
              transform: "scale(1.1)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : <TableChart fontSize="medium" />}
        </IconButton>
      </Tooltip>
    </Grid>
  );
};

export default PayInDownloadButtons;
